import {
  DATE_FORMATS,
  DateTime,
  dayOfWeekName,
  MonthYearRecurrence,
  TIME_FORMATS
} from "@bps/date-time";
import {
  CalendarEventStatus,
  CalendarEventStatusText,
  Frequency,
  RecurrenceLinkingActionType,
  RecurrenceRuleDto
} from "@libs/gateways/booking/BookingGateway.dtos.ts";
import { toRecurrenceOptions } from "@libs/utils/calendar/calendar.utils.ts";
import { AppointmentFormValues } from "@shared-types/booking/appointment-form-values.types.ts";
import { EndScheduleType } from "@shared-types/booking/end-schedule.constant.ts";
import { BookingStore } from "@stores/booking/BookingStore.ts";
import {
  AvailabilitySlot,
  SlotGroup
} from "@stores/booking/models/AvailabilitySlots.ts";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";

import { AppointmentStatusTextValues } from "../../../shared-components/appointment-status/utils.ts";
import {
  getCount,
  getDayRecur,
  getMonthDayRecur,
  getMonthRecur,
  getRecurrenceWeekPosition,
  getUntil
} from "../../../utils.tsx";

export const recurrenceRuleDto = (
  values: AppointmentFormValues,
  overrideCount: number | undefined = undefined
): RecurrenceRuleDto => {
  const frequency = Number(values.frequency);
  const startDate = DateTime.fromJSDate(values.startDate!);

  const recurrenceRule = {
    count: overrideCount ?? getCount(values.endScheduleType, values.count),
    frequency,
    weekPosition: getRecurrenceWeekPosition(
      startDate,
      values.frequency,
      values.monthYearRecurrence
    ),
    dayRecur: getDayRecur({
      startDate,
      frequency: values.frequency,
      dayRecur: values.dayRecur,
      monthYearRecurrence: values.monthYearRecurrence
    }),
    monthDayRecur: getMonthDayRecur(
      startDate,
      values.frequency,
      values.monthYearRecurrence
    ),
    interval: values.interval!,
    monthRecur: getMonthRecur(frequency, startDate),
    until: getUntil(values.endScheduleType, values.until)
  };

  return recurrenceRule;
};

export const getGroupByCountSlices = (
  availabilitySlot: AvailabilitySlot[],
  slotSize: number
): SlotGroup[] => {
  const slotGroup: SlotGroup[] = [];
  const arrayLength = availabilitySlot.length;
  let arrayIndex = 0;
  while (arrayIndex < arrayLength) {
    const output: SlotGroup = {
      time: []
    };
    availabilitySlot
      .slice(arrayIndex, (arrayIndex += slotSize))
      .forEach(value => {
        output.time.push(value);
      });
    slotGroup.push(output);
  }
  return slotGroup;
};

export const getTimeSpan = (minutes: number): string => {
  const hourInMinutes = Number(60);
  return `00.${Math.floor(minutes / hourInMinutes)}:${
    minutes % hourInMinutes
  }:00`;
};

export const getRecurrenceRule = (values: AppointmentFormValues) => {
  const item = recurrenceRuleDto(values);
  if (
    !values.repeat ||
    (!item.dayRecur && !item.monthDayRecur) ||
    !values.endScheduleType ||
    (values.endScheduleType === EndScheduleType.OnDate && !item.until) ||
    (values.endScheduleType === EndScheduleType.After && !item.count)
  ) {
    return undefined;
  }

  const recurrenceRule = {
    frequency: item.frequency,
    until: item.until ? DateTime.fromISO(item.until) : undefined,
    count:
      item.until === undefined && item.count === undefined ? 1 : item.count,
    dayRecur: item.dayRecur,
    interval: item.interval,
    monthDayRecur: item.monthDayRecur,
    monthRecur: item.monthRecur,
    start:
      values.startDate &&
      DateTime.fromMillis(
        values.startDate.setHours(
          values.startDate && (values.startDate.getTimezoneOffset() / 60) * -1
        )
      ),
    weekPosition: item.weekPosition
  };

  return recurrenceRule;
};

export const formatTimeSpanWithDate = (
  time: string,
  formatOptions: {
    date?: Date | undefined;
    duration?: number;
  }
) => {
  let parsedTime = DateTime.fromParsedTimeString(time);
  if (!parsedTime) return undefined;

  const parsedDate = DateTime.fromJSDate(formatOptions.date);
  if (parsedDate) {
    parsedTime = parsedTime.set({
      year: parsedDate.year,
      month: parsedDate.month,
      day: parsedDate.day
    });
  }

  parsedTime = parsedTime.plus({ minutes: formatOptions.duration ?? 0 });

  return parsedTime.toFormat(TIME_FORMATS.DEFAULT_TIME_FORMAT);
};

export const getLinkedOccurrenceCount = (
  calendarEvents: CalendarEvent[] | undefined
): number => {
  if (!calendarEvents) {
    return 0;
  }
  return calendarEvents.reduce((count, calendarEvent) => {
    if (calendarEvent.episodeOfCareId) {
      return count + 1;
    } else {
      return count;
    }
  }, 0);
};

export const getRecurrenceLinkText = (
  displayedCondition: string,
  occurrencesOptions: {
    totalOccurrences: number;
    recurrenceOccurred: number;
  },

  linkingAction?: RecurrenceLinkingActionType
): string => {
  let message: string = "";

  const { totalOccurrences, recurrenceOccurred } = occurrencesOptions;

  if (totalOccurrences > 1) {
    if (linkingAction === RecurrenceLinkingActionType.LinkAll) {
      message = `${totalOccurrences} appointments linked to ${displayedCondition}`;
    } else if (linkingAction === RecurrenceLinkingActionType.UnlinkAll) {
      message = `0 appointments linked to ${displayedCondition}`;
    } else {
      message = `${recurrenceOccurred} of ${totalOccurrences} appointments linked to ${displayedCondition}`;
    }
  }

  return message;
};

export const getAppointmentStatusFromCode = (code: string, type: string) => {
  return type === CalendarEventStatus.Confirmed
    ? AppointmentStatusTextValues[
        code as keyof typeof AppointmentStatusTextValues
      ]
    : CalendarEventStatusText[type as keyof typeof CalendarEventStatusText];
};

interface RecurrenceData {
  frequency: number | undefined;
  interval: number | undefined;
  dayRecur: number[] | undefined;
  startDate: Date | undefined;
  monthYearRecurrence: MonthYearRecurrence | undefined;
}

interface EndScheduleData {
  endScheduleType: string | undefined;
  count: number | undefined;
  until: Date | undefined;
  recurrenceOccurred: number | undefined;
}

export const getEndSchedduleTypeText = ({
  endScheduleType,
  count,
  until,
  recurrenceOccurred
}: EndScheduleData) => {
  if (endScheduleType === EndScheduleType.After) {
    return count ? `- ${recurrenceOccurred ?? 1} of ${count} occurrences` : "";
  }

  if (endScheduleType === EndScheduleType.OnDate) {
    return until
      ? `until ${DateTime.fromJSDate(until).toFormat(
          DATE_FORMATS.DAY_TEXT_MONTH_YEAR
        )}`
      : "";
  }

  return "";
};

const getDays = ({ frequency, interval, dayRecur }: RecurrenceData) => {
  if (frequency === Frequency.Week && (!interval || interval === 1)) {
    if (dayRecur?.length === 7) {
      return " day";
    }

    if (
      dayRecur?.length === 5 &&
      dayRecur.includes(1) &&
      dayRecur.includes(2) &&
      dayRecur.includes(3) &&
      dayRecur.includes(4) &&
      dayRecur.includes(5)
    ) {
      return " weekday";
    }
  }

  return ` ${dayRecur?.map(dayOfWeekName).join(", ")}`;
};

const getFrequencyText = ({ frequency, interval }: RecurrenceData) => {
  if (frequency === Frequency.Week) {
    return interval && interval > 1 ? " weeks on" : "";
  }

  if (frequency === Frequency.Month) {
    return interval && interval > 1 ? " months on the" : " month on the";
  }

  if (frequency === Frequency.Year) {
    return interval && interval > 1 ? " years on the" : " year on the";
  }

  return "";
};

const getIntervalText = (recurrence: RecurrenceData) => {
  if (recurrence.interval === 1) {
    return `every${getFrequencyText(recurrence)}`;
  }

  return `every ${recurrence.interval}${getFrequencyText(recurrence)}`;
};

export const getRecurrenceNaturalText = (
  recurrence: RecurrenceData
): string => {
  const { frequency, monthYearRecurrence, startDate } = recurrence;

  if (frequency === Frequency.Week) {
    return `${getIntervalText(recurrence)}${getDays(recurrence)}`;
  }

  if (!startDate) return "";

  if (frequency === Frequency.Month) {
    const recurrenceOptions = toRecurrenceOptions(
      DateTime.fromJSDate(startDate),
      Frequency.Month,
      true
    );

    const textMonth = recurrenceOptions.find(
      x => x.key === monthYearRecurrence
    );
    return `${getIntervalText(recurrence)} ${
      monthYearRecurrence ? textMonth?.text : ""
    }`;
  }

  if (frequency === Frequency.Year) {
    const recoptions = toRecurrenceOptions(
      DateTime.fromJSDate(startDate),
      Frequency.Year,
      true
    );

    const textYear = recoptions.find(x => x.key === monthYearRecurrence);

    return `${getIntervalText(recurrence)} ${
      monthYearRecurrence ? textYear?.text : ""
    }`;
  }
  return "";
};

export const getAvailableSlots = async (
  booking: BookingStore,
  args: {
    userId: string;
    orgUnitIds: string[];
    startDate: DateTime;
    duration: string | number;
    calendarEvent: CalendarEvent | undefined;
  }
) => {
  const { userId, orgUnitIds, startDate, duration, calendarEvent } = args;

  const result = await booking.getUserAvailabilitySlot({
    userId,
    orgUnitIds,
    startDate: startDate.startOf("day"),
    endDate: startDate.plus({ months: 3 }).endOf("month"),
    duration: getTimeSpan(Number(duration))
  });

  if (calendarEvent) {
    const startOfDay = calendarEvent.startDateTime.startOf("day");

    result.dto.forEach(slotDto => {
      if (slotDto.orgUnitId !== calendarEvent?.orgUnitId) {
        if (DateTime.fromISO(slotDto.date).equals(startOfDay)) {
          slotDto.availableTimes.push(
            calendarEvent?.startDateTime.toFormat("HH:mm") ?? ""
          );
          slotDto.availableTimes.sort(); // sort alphabetically
        }
      }
    });
  }

  return result;
};

export const durationOptions = [
  { key: "0-15", text: "Under 15 min" },
  { key: "15-30", text: "15 – 30 min" },
  { key: "30-45", text: "30 – 45 min" },
  { key: "45-60", text: "45 – 60 min" },
  { key: "60+", text: "60+ min" }
];

export const durationMap: Record<string, { min: number; max?: number }> = {
  "0-15": { min: 0, max: 15 },
  "15-30": { min: 15, max: 30 },
  "30-45": { min: 30, max: 45 },
  "45-60": { min: 45, max: 60 },
  "60+": { min: 60 }
};
